import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { TypographyProps } from '@mui/material/Typography';

export type EllipsisWithTooltipProps = {
  /** Raw text; trimmed for display and tooltip */
  text: string;
  variant?: TypographyProps['variant'];
  typographySx?: SxProps<Theme>;
  /**
   * When `text` is empty/whitespace only, show this instead (e.g. "—").
   * Omit or pass "" to render an empty line without a dash.
   */
  emptyLabel?: string;
  /**
   * When set and different from displayed text, always show this on hover
   * (e.g. full unrounded numeric value while display is abbreviated).
   */
  tooltipTitle?: string;
};

/**
 * Single-line ellipsis; shows a MUI Tooltip with the full string only when the line is clipped.
 * Use in table cells and anywhere long labels need hover-to-read behavior.
 */
export function EllipsisWithTooltip({
  text,
  variant = 'body2',
  typographySx,
  emptyLabel = '',
  tooltipTitle,
}: EllipsisWithTooltipProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setIsOverflowing(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [text, emptyLabel, measure]);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  const asString = typeof text === 'string' ? text : text == null ? '' : String(text);
  const trimmed = asString.trim();
  const display = trimmed.length > 0 ? trimmed : emptyLabel;

  const body = (
    <Typography
      ref={ref}
      component="span"
      variant={variant}
      sx={{
        display: 'block',
        minWidth: 0,
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        ...typographySx,
      }}
    >
      {display}
    </Typography>
  );

  if (!display) {
    return (
      <Typography component="span" variant={variant} sx={{ ...typographySx, display: 'block', minWidth: 0 }} />
    );
  }

  const fullTooltip = (typeof tooltipTitle === 'string' ? tooltipTitle : tooltipTitle == null ? '' : String(tooltipTitle)).trim();
  const forceFullValueTooltip = fullTooltip.length > 0 && fullTooltip !== display;

  if (!isOverflowing && !forceFullValueTooltip) {
    return body;
  }

  return (
    <Tooltip
      title={forceFullValueTooltip ? fullTooltip : display}
      arrow
      placement="top"
      enterDelay={300}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 480,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          },
        },
      }}
    >
      <Box component="span" sx={{ display: 'block', minWidth: 0, maxWidth: '100%' }}>
        {body}
      </Box>
    </Tooltip>
  );
}
